<template>
  <div class="message-item" :class="message.role">
    <div class="content-wrapper" :aria-busy="message.status === 'sending'">
      <!-- 根据消息内容切换思考和正文阶段，演示与真实消息共用展示逻辑。 -->
      <div v-if="message.role === 'assistant' && message.reasoning" class="reasoning">
        <button
          type="button"
          class="reasoning-toggle"
          :aria-expanded="reasoningExpanded"
          @click="reasoningExpanded = !reasoningExpanded"
        >
          <i :class="isReasoning ? 'el-icon-loading' : 'icon icon-sikao'" class="reasoning-icon" aria-hidden="true"></i>
          <span>{{ isReasoning ? $t('messageItem.shen_du_si_kao_zhong') : $t('messageItem.sdskgc') }}</span>
          <i :class="reasoningExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" aria-hidden="true"></i>
        </button>
        <el-collapse-transition>
          <div v-show="reasoningExpanded">
            <div class="reasoning-content">{{ message.reasoning }}</div>
          </div>
        </el-collapse-transition>
      </div>

      <!-- 等待状态下的进度（如：正在分析您的问题...）：只展示当前一条，
           新进度从下方滚入并覆盖上一条，避免状态列表越堆越长。 -->
      <div
        v-if="currentProgress"
        class="progress-roller"
        role="status"
        aria-live="polite"
        :aria-label="$t('messageItem.zzsc')"
      >
        <transition name="progress-roll">
          <div
            :key="currentProgress.key"
            class="progress-item"
            :class="{ 'is-done': currentProgress.done, 'is-active': !currentProgress.done }"
          >
            <span class="progress-mark" aria-hidden="true">
              <!-- 设计稿进度圆点内是 icon-sikao：进行中旋转，完成后停止 -->
              <i class="icon icon-sikao"></i>
            </span>
            <span class="progress-text">{{ currentProgress.text }}</span>
          </div>
        </transition>
      </div>

      <div class="content" :class="{ 'has-text': !!message.content }">
        <!-- AI 回答保留 Markdown 渲染，并在输出前清理 HTML。 -->
        <div
          v-if="message.role === 'assistant' && message.content"
          class="markdown-body"
          v-html="renderedContent"
        ></div>
        <div v-else-if="message.role === 'user' && message.content" class="plain-text">{{ message.content }}</div>
        <!-- 没有进度事件时，回退到通用打字动画 -->
        <div v-else-if="isThinking && !message.reasoning" class="typing-indicator" role="status" :aria-label="$t('messageItem.zzsc')">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span v-if="message.role === 'assistant' && isThinking && message.content" class="thinking-cursor" aria-hidden="true"></span>

        <div v-if="message.status === 'error'" class="error-tip">
          <i class="el-icon-warning-outline" aria-hidden="true"></i>
          <span>{{ $t('messageItem.qing_qiu_shi_bai') }}</span>
          <el-button size="mini" type="text" @click="$emit('retry', message)">{{ $t('messageItem.chong_shi') }}</el-button>
        </div>
      </div>

      <!-- 提问气泡下方提供复制入口，点击复制提问原文 -->
      <div v-if="message.role === 'user' && message.content" class="message-actions user-actions">
        <el-tooltip :content="$t('messageItem.fu_zhi')" placement="top">
          <button type="button" class="copy-button" :aria-label="$t('messageItem.fu_zhi')" @click="copyMessage">
            <i class="icon icon-copy1" aria-hidden="true"></i>
          </button>
        </el-tooltip>
      </div>

      <div v-if="message.role === 'assistant' && (message.status === 'done' || message.status === 'stopped')" class="message-actions">
        <span v-if="message.status === 'done'" class="success-tip" :class="{ 'is-celebrating': showSuccess }" role="status">
          <span class="success-mark" aria-hidden="true">
            <i class="el-icon-check"></i>
          </span>
        <span class="success-label">{{ $t('messageItem.scwc') }}</span>
        </span>
        <span v-else-if="message.status === 'stopped'" class="stopped-tip" role="status">{{ $t('messageItem.ytzsc') }}</span>
          <!-- 复制按钮暂时隐藏：AI 返回 HTML 富文本时复制出来的是 div 标签，暂时用不上 -->
        <!-- <el-tooltip v-if="message.content" content="复制" placement="top">
          <button type="button" class="copy-button" aria-label="复制" @click="copyMessage">
            <i class="icon icon-copy1" aria-hidden="true"></i>
          </button>
        </el-tooltip> -->
          <el-tooltip v-if="message.status === 'stopped'" :content="$t('messageItem.chong_shi')" placement="top">
          <button type="button" class="copy-button" :aria-label="$t('messageItem.chong_shi')" @click="$emit('retry', message)">
            <i class="icon icon-chongxin" aria-hidden="true"></i>
          </button>
        </el-tooltip>
        <!-- 结果反馈：点赞/点踩，再次点击同一项可取消反馈 -->
        <el-tooltip v-if="message.content" :content="$t('messageItem.dian_zan')" placement="top">
          <button
            type="button"
            class="copy-button feedback-button"
            :class="{ 'is-active': feedback === 1 }"
            :aria-pressed="feedback === 1"
            :aria-label="$t('messageItem.dian_zan')"
            @click="handleFeedback(1)"
          >
            <!-- viewBox 裁掉路径自带的空白边距，让点赞图标墨迹与设计稿一致为 18×18 -->
            <svg class="action-icon" viewBox="1.2 1.2 21.6 21.6" width="14" height="14" fill="none" aria-hidden="true">
              <path
                d="M7 10v11M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h1a2 2 0 0 1 2 2z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </el-tooltip>
        <el-tooltip v-if="message.content" :content="$t('messageItem.dian_cai')" placement="top">
          <button
            type="button"
            class="copy-button feedback-button"
            :class="{ 'is-active': feedback === 2 }"
            :aria-pressed="feedback === 2"
            :aria-label="$t('messageItem.dian_cai')"
            @click="handleFeedback(2)"
          >
            <!-- viewBox 裁掉路径自带的空白边距，让点踩图标墨迹与设计稿一致为 18×18 -->
            <svg class="action-icon" viewBox="1.2 1.2 21.6 21.6" width="14" height="14" fill="none" aria-hidden="true">
              <path
                d="M17 14V3M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h-1a2 2 0 0 1-2-2z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import apis from '@/utils/apis';

const md = new MarkdownIt({
  // 允许渲染 AI 返回的 HTML 富文本（后端可能直接返回 HTML 格式的回答），
  // 输出前仍经过 DOMPurify 消毒，防止 XSS
  html: true,
  breaks: true, // 将换行符转换为 <br>
  linkify: true, // 自动识别 URL 并转换为链接
  typographer: true, // 启用智能标点替换
})
  .enable('table') // 显式启用 GFM 表格支持
  .enable('strikethrough'); // 启用删除线（~~text~~）

export default {
  name: 'MessageItem',
  props: {
    message: {
      type: Object,
      required: true,
    },
    userAvatar: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      reasoningExpanded: false,
      // 本地记录本次会话提交的反馈，未提交前取历史消息的 feedback 字段
      localFeedback: null,
      showSuccess: false,
      successTimer: null,
    };
  },
  computed: {
    isThinking() {
      return this.message.role === 'assistant' && this.message.status === 'sending';
    },
    isReasoning() {
      return this.isThinking && !!this.message.reasoning && !this.message.content;
    },
    renderedContent() {
      if (!this.message.content) {
        return '';
      }
      // AI 返回的 HTML 富文本常带有缩进，markdown 会把 4 空格缩进的行当成代码块，
      // 导致 HTML 标签被当文本原样显示；渲染前先去掉标签行的前导缩进
      return DOMPurify.sanitize(md.render(this.normalizeHtmlIndent(this.message.content)));
    },
    // 后端真实消息 ID（ai_chat_message.msgId，int64）；
    // 流式回答的 msgId 在 [DONE]:会话ID,消息ID 事件中返回，历史消息直接取 msgId 字段
    realMsgId() {
      if (this.message.msgId) {
        return String(this.message.msgId);
      }
      // 本地临时 ID（如 assistant-开头）不是后端消息，不能用于反馈
      return (/^\d+$/).test(String(this.message.id)) ? String(this.message.id) : null;
    },
    // 当前反馈状态：0-无反馈 / 1-点赞 / 2-点踩
    feedback() {
      return this.localFeedback !== null ? this.localFeedback : (this.message.feedback || 0);
    },
    progressList() {
      const list = this.message.progressList || [];
      if (list.length) {
        return list;
      }
      // 接口未返回进度列表时，兼容旧的单个进度文案
      return this.message.progress ? [{ text: this.message.progress, done: false }] : [];
    },
    // 等待状态只展示最新一条进度，key 变化时触发滚动切换动画；
    // 回答结束（完成 / 停止 / 失败）后不再展示检索进度
    currentProgress() {
      if (!this.isThinking) {
        return null;
      }
      const index = this.progressList.length - 1;
      if (index < 0) {
        return null;
      }
      const item = this.progressList[index];
      return { ...item, key: `${index}-${item.text}` };
    },
  },
  watch: {
    isReasoning: {
      immediate: true,
      handler(value) {
        this.reasoningExpanded = value;
      },
    },
    // AI 回答每次流式更新都会重建 v-html 的 DOM，
    // 渲染后统一给表格包一层滚动容器（详见 wrapTables）
    renderedContent: {
      immediate: true,
      handler() {
        this.$nextTick(this.wrapTables);
      },
    },
    'message.status'(status, previous) {
      clearTimeout(this.successTimer);
      this.showSuccess = this.message.role === 'assistant' && previous === 'sending' && status === 'done';
      if (this.showSuccess) {
        this.successTimer = setTimeout(() => {
          this.showSuccess = false;
        }, 1600);
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.successTimer);
  },
  methods: {
    // 去掉 HTML 标签行（<div、</div> 等）行首的缩进，避免被 markdown 解析成代码块；
    // 代码块围栏（``` / ~~~）内的内容是用户有意展示的代码，保持原样不处理
    normalizeHtmlIndent(content) {
      let inCodeFence = false;
      return String(content)
        .split('\n')
        .map((line) => {
          if ((/^\s*(```|~~~)/).test(line)) {
            inCodeFence = !inCodeFence;
            return line;
          }
          if (!inCodeFence && (/^\s+<\/?[a-zA-Z]/).test(line)) {
            return line.replace(/^\s+/, '');
          }
          return line;
        })
        .join('\n');
    },
    // AI 返回的表格要同时满足两点：内容窄时撑满消息区、内容宽时自身横向滚动。
    // 直接给 table 加 overflow 无效（表格盒不是滚动容器），
    // 改 display:block 又会让列按内容宽度收缩、无法撑满（用户反馈的右侧留白）。
    // 因此在渲染后把表格包一层 .md-table-wrap 滚动容器，表格恢复 100% 宽度。
    // v-html 每次更新都会重建 DOM，已包裹的表格跳过，保证重复渲染时幂等。
    wrapTables() {
      if (!this.$el) {
        return;
      }
      this.$el.querySelectorAll('.markdown-body table').forEach((table) => {
        const parent = table.parentElement;
        if (!parent || parent.classList.contains('md-table-wrap')) {
          return;
        }
        const wrap = document.createElement('div');
        wrap.className = 'md-table-wrap';
        parent.insertBefore(wrap, table);
        wrap.appendChild(table);
      });
    },
    // 提交点赞/点踩反馈，再次点击同一项则取消反馈（feedback 传 0）
    handleFeedback(type) {
      const next = this.feedback === type ? 0 : type;
      // 演示入口未注入 $postJSON，无法提交反馈
      if (!this.$postJSON) {
        return;
      }
      // 接口要求 msgId 为后端持久化的 int64 消息 ID，
      // 流式回答在 [DONE]:会话ID,消息ID 中返回消息 ID，历史消息用消息列表返回的 msgId
      if (!this.realMsgId) {
        this.$message.warning(this.$t('messageItem.dqhdzbzcfkshcxjrhhzs'));
        return;
      }
      this.$postJSON(apis.AiChatFeedback, {
        msgId: this.realMsgId, // 消息 ID（字符串传输，避免 int64 超出 JS 安全整数丢精度）
        feedback: next, // 0-取消反馈 / 1-点赞 / 2-点踩
      }).then(() => {
        this.localFeedback = next;
        if (next === 0) {
          this.$message.success(this.$t('messageItem.yi_qu_xiao_fan_kui'));
        } else {
          this.$message.success(this.$t('messageItem.gxndfk'));
        }
      }).catch(() => {
        this.$message.error(this.$t('messageItem.fktjsbqcs'));
      });
    },
    copyMessage() {
      const text = this.message.content;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(this.$t('messageItem.yi_fu_zhi'));
        }).catch(() => {
          this.copyByTextarea(text);
        });
        return;
      }
      this.copyByTextarea(text);
    },
    // 本地 HTTP 或浏览器未授权剪贴板时，使用临时文本框兼容复制。
    copyByTextarea(text) {
      const activeElement = document.activeElement;
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.readOnly = true;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      } finally {
        document.body.removeChild(textarea);
        if (activeElement && activeElement.focus) {
          activeElement.focus();
        }
      }
      if (copied) {
        this.$message.success(this.$t('messageItem.yi_fu_zhi'));
      } else {
        this.$message.error(this.$t('messageItem.fzsbqcs'));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-item {
  display: flex;
  width: 100%;
  min-width: 0;
  // 设计稿消息区为纵向排布、模块间距 16
  margin-bottom: 16px;
  padding: 0 16px;
  box-sizing: border-box;
  color: #333333;
  letter-spacing: 0;
  animation: message-fade-in 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  .content-wrapper {
    min-width: 0;
    width: 100%;
  }

  .content {
    min-width: 0;
    font-size: 14px;
    line-height: 19.2px;
    overflow-wrap: break-word;
  }

  &.user {
    justify-content: flex-end;

    .content-wrapper {
      width: auto;
      max-width: 85%;
    }

    // 设计稿用户气泡：底色 #F5F6FA、文字 #1B1433、圆角 18/4、内边距 19/12
    .content.has-text {
      padding: 19px 12px;
      border-radius: 18px 4px 18px 18px;
      background: #f5f6fa;
      color: #1b1433;
    }
  }

  &.assistant {
    justify-content: flex-start;

    .content-wrapper {
      width: auto;
      max-width: 100%;
    }

    // 设计稿 AI 回答不带气泡底色，正文直接铺在白色背景上
    .content.has-text {
      padding: 0;
      color: #333333;
    }
  }

  .plain-text {
    white-space: pre-wrap;
    word-break: break-word;
  }
}

@keyframes message-fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 设计稿思考区块与正文之间间距 16
.reasoning {
  margin-bottom: 16px;
}

// 设计稿思考头部为无边框纯行：思考图标 + 文字 13px/16.8（黑色 65%） + 展开箭头
.reasoning-toggle {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 25px;
  padding: 0;
  border: 0;
  background: transparent;
  // 设计稿文字填充为 #000000 @65%，白底等效 #595959（思考内容块才是 #848691）
  color: rgba(0, 0, 0, 0.65);
  font: inherit;
  font-size: 13px;
  line-height: 16.8px;
  text-align: left;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  // 设计稿展开箭头 18×18、与文字间距 6、色值 #8C8C8C
  i {
    flex-shrink: 0;
    margin-left: 6px;
    color: #8c8c8c;
  }

  // 设计稿思考图标为 14×14 的原子图标、色值 #0670FF，不带外圈圆圈
  .reasoning-icon {
    flex-shrink: 0;
    margin-right: 6px;
    margin-left: 0;
    color: #0670ff;
    font-size: 14px;
  }

  .el-icon-loading { animation: reasoning-loading 1.2s linear infinite; }

  &:hover,
  &:focus-visible {
    color: #0d9ae0;

    .reasoning-icon { color: #0d9ae0; }
  }
}

// 设计稿思考内容：底色 #F8F9FB、圆角 8、文字 13px/22 #848691
.reasoning-content {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8f9fb;
  color: #848691;
  font-size: 13px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

// 设计稿操作行：图标 18×18、间距 8，与正文间距 16
.message-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  margin-top: 16px;
}

// 提问下方的复制行：靠右对齐，与气泡间距收紧
.user-actions {
  justify-content: flex-end;
  min-height: 0;
  margin-top: 6px;
}

.success-tip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #10b981;
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;

  &.is-celebrating { animation: chat-success-in 0.35s ease-out; }
  &.is-celebrating .success-mark { animation: success-mark-pop 0.55s ease-out; }
  &.is-celebrating .success-mark::before { animation: success-ring 0.8s ease-out; }
  &.is-celebrating .success-mark i { animation: success-check-in 0.45s 0.12s ease-out both; }
  &.is-celebrating .success-label { animation: success-label-in 0.35s 0.18s ease-out both; }
}

.success-mark {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1.5px solid #10b981;
  border-radius: 50%;
  color: #059669;
  font-size: 11px;
  line-height: 1;

  &::before {
    position: absolute;
    inset: -4px;
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 50%;
    content: '';
    opacity: 0;
  }
}

.success-mark i { display: block; }
.stopped-tip { color: #959ba4; font-size: 12px; line-height: 20px; }

.copy-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  // 设计稿按钮热区 18x18、图标色值 #848691
  color: #848691;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  // 字体图标随按钮颜色变化，保持 hover 一起变深
  .icon {
    display: block;
    // 设计稿复制图标墨迹 14.64×14.64；icon-copy 字形墨迹占字号 0.977，15px 约 14.7
    font-size: 15px;
  }

  // 设计稿刷新图标墨迹 14.26×14.26；icon-chongxin 字形墨迹占字号 0.793，18px 约 14.3
  .icon-chongxin { font-size: 18px; }

  // 点赞/点踩用的内联 SVG，去掉基线间隙
  .action-icon {
    display: block;
  }

  &:hover,
  &:focus-visible {
    color: #333333;
  }
}

// 反馈按钮选中态：主题蓝高亮，与思考图标色值保持一致
.feedback-button.is-active {
  color: #0670ff;

  &:hover,
  &:focus-visible {
    color: #0670ff;
  }
}

.error-tip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  color: #f56c6c;
  font-size: 12px;

  .el-button {
    margin-left: 2px;
    padding: 0;
    color: #f56c6c;
    line-height: 23px;
  }
}

.typing-indicator {
  display: flex;
  align-items: center;
  height: 23px;
  gap: 5px;

  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #999999;
    animation: typing 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

// 滚动窗口：始终只保留一行高度，超出部分裁掉，形成“新进度覆盖旧进度”的效果
.progress-roller {
  position: relative;
  box-sizing: border-box;
  max-width: 380px;
  min-height: 32px;
  margin: 0 0 14px 4px;
  overflow: hidden;
}

.progress-item {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 32px;
  gap: 10px;
  padding: 2px 0;
  box-sizing: border-box;
  // 进度文字与思考内容文字统一为设计稿浅灰 #848691
  color: #848691;
  font-size: 13px;
  line-height: 20px;
  transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  // 已完成的进度变灰
  &.is-done {
    color: #a8abb7;

    .progress-text {
      animation: progress-complete-in 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
}

// 设计稿里思考/进度状态就是纯原子图标，没有外圈圆环、没有扩散光圈
.progress-mark {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  // 设计稿蓝色 #0670FF
  color: #0670ff;
  line-height: 1;
  transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  // 设计稿思考/进度图标为 14×14，与「深度思考过程」行保持一致
  .icon-sikao {
    font-size: 14px;
    color: #0670ff;
    animation: progress-loading 1s linear infinite;
  }

  // 完成状态：图标保持设计稿蓝色 #0670FF，仅保留弹入动画
  .is-done & {
    animation: progress-mark-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);

    // 完成后停止旋转，改为弹入动画
    .icon-sikao {
      animation: progress-check-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
  }
}

.progress-text {
  min-width: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 20px;
  max-height: 40px;
  word-break: break-word;

  // 已完成项颜色变浅，字体恢复常规
  .is-done & {
    color: #a0a3b0;
    font-weight: 400;
  }

  // 当前步骤：设计稿思考行文字为 13px / 400 / #000000 @65%（白底 #595959）
  .is-active & {
    color: rgba(0, 0, 0, 0.65);
    font-weight: 400;
    animation: progress-text-pulse 2s ease-in-out infinite;
  }
}

.thinking-cursor {
  display: inline-block;
  width: 7px;
  height: 16px;
  margin-top: 6px;
  border-radius: 4px;
  background: #326cd1;
  vertical-align: middle;
  animation: thinking-cursor-blink 1s steps(2) infinite;
}

@keyframes typing {
  0%, 60%, 100% {
    opacity: 0.35;
  }
  30% {
    opacity: 1;
  }
}

@keyframes progress-loading {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// 进度切换：上一条向上滚出，新一条从下方滚入覆盖在它上面
.progress-roll-enter-active,
.progress-roll-leave-active { transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
// 移出的条目脱离文档流，让新条目覆盖在同一行
.progress-roll-leave-active {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
}
.progress-roll-enter { opacity: 0; transform: translateY(100%); }
.progress-roll-leave-to { opacity: 0; transform: translateY(-100%); }

@keyframes progress-mark-pop {
  0% { opacity: 0.4; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes progress-mark-bounce {
  0% { transform: scale(0.6); }
  40% { transform: scale(1.2); }
  60% { transform: scale(0.95); }
  80% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes progress-text-pulse {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
}

@keyframes progress-complete-in {
  0% { opacity: 0.7; }
  100% { opacity: 1; }
}

@keyframes progress-check-in {
  0% { opacity: 0; transform: scale(0.3) rotate(-15deg); }
  60% { opacity: 1; transform: scale(1.25) rotate(10deg); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
}

@keyframes reasoning-loading {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes thinking-cursor-blink {
  50% { opacity: 0; }
}

@keyframes chat-success-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes success-mark-pop {
  0% { opacity: 0; transform: scale(0.55); }
  65% { opacity: 1; transform: scale(1.16); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes success-ring {
  0% { opacity: 0.75; transform: scale(0.6); }
  100% { opacity: 0; transform: scale(1.8); }
}

@keyframes success-check-in {
  0% { opacity: 0; transform: scale(0.3) rotate(-25deg); }
  70% { opacity: 1; transform: scale(1.15) rotate(4deg); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
}

@keyframes success-label-in {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}

.markdown-body {
  min-width: 0;
  // 设计稿正文：14px / 行高 19.2
  font-size: 14px;
  line-height: 19.2px;
  overflow-wrap: break-word;
  // AI 返回的 HTML 富文本可能包含固定宽度的多列布局，
  // 容器不够宽时整体横向滚动，避免内容溢出消息区
  overflow-x: auto;

  // AI 的回答是按像素精排的「设计卡片」（柱状图、概览、明细表），原始按 750px 宽设计。
  // 窄面板放不下，这里让卡片「等比自适应」收进面板，而不是裁切、也不是让子行各自滚动：
  //  1) 顶层卡片宽度收进面板；
  //  2) 卡片内带固定像素宽度的元素（如柱子 width:72px）一律不超过所在容器 → 自适应收窄，
  //     使 flex:1 等分的柱状图整体缩放，柱子与日期标签始终对齐、不溢出不错位。
  // 两条红线：绝不加 flex-wrap:wrap（子项换行叠加会错位）；绝不给内部 flex 行各自挂
  // overflow（会让「柱子行」和「日期行」各自横向滚动、宽度不一致而错位 —— 用户反馈的错位）。
  ::v-deep > div {
    max-width: 100%;
  }
  ::v-deep [style*='width'] {
    max-width: 100%;
  }

  // 滚动条样式见本文件末尾「非 scoped」样式块：那里用 .markdown-body 普通类选择器统一处理，
  // 不依赖 scoped 的 data-v，能可靠命中 v-html 动态内容（表格等）里的滚动条。
  // （scoped 下 ::v-deep 紧跟 ::-webkit-scrollbar 伪元素时，本项目构建会编译/匹配不稳定。）

  // v-html 的节点没有 scoped 标记，需要深度选择器才能应用正文样式。
  ::v-deep {
    p {
      // 设计稿段落之间间距 10
      margin: 0 0 10px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    code {
      padding: 2px 4px;
      border-radius: 3px;
      background: #f5f6fa;
      color: #374151;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 13px;
    }

    pre {
      max-width: 100%;
      margin: 12px 0;
      padding: 12px;
      border: 1px solid #e3e5eb;
      border-radius: 4px;
      background: #f7f8fa;
      box-sizing: border-box;
      overflow-x: auto;
      white-space: pre;
      word-break: normal;
      overflow-wrap: normal;

      code {
        padding: 0;
        background: transparent;
        color: #1f2937;
      }
    }

    // 设计稿列表：整体上下间距 10、左缩进 20、列表项之间间距 6
    ul, ol {
      margin: 10px 0;
      padding-left: 20px;

      li {
        margin: 0;

        & + li {
          margin-top: 6px;
        }
      }
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    // 表格本身恢复真实表格布局并撑满容器：列宽自动分配，窄表格不再右侧留白。
    // 滚动能力交给外层 .md-table-wrap（见 wrapTables）：
    // 内容实在放不下、表格仍超出面板时，在容器内部横向滚动，不撑开整条消息。
    table {
      display: table;
      width: 100%;
      margin: 0;
      border: 1px solid #e3e5eb;
      border-radius: 6px;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 13px;
    }

    // 表格滚动容器：面板宽时表格 100% 撑满，面板放不下时表格在容器内部横向滚动
    .md-table-wrap {
      max-width: 100%;
      margin: 16px 0;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    // 单元格左右内边距固定兜底：AI 返回的表格常在内联样式里写 padding: Npx 0，
    // 横向内边距会被覆盖，文字直接贴到表格左右边缘（看起来没有缩进）
    th, td {
      padding: 10px 16px;
      padding-left: 16px !important;
      padding-right: 16px !important;
      border-bottom: 1px solid #e3e5eb;
      border-right: 1px solid #e3e5eb;
      text-align: left;
      // 不锁 nowrap：内容不可换行时表格会超出面板宽度，右侧单元格连内边距一起被裁掉；
      // 允许换行后列宽可以收窄，表格始终贴合面板，左右缩进都能完整显示
      white-space: normal !important;
      vertical-align: middle;

      &:last-child {
        border-right: 0;
      }
    }

    th {
      background: #f5f7fa;
      color: #1f2937;
      font-weight: 600;
      font-size: 13px;
    }

    tr:last-child td {
      border-bottom: 0;
    }

    tbody tr {
      background: #ffffff;
      transition: background 0.2s ease;

      &:hover {
        background: #f9fafb;
      }

      &:nth-child(even) {
        background: #fafbfc;

        &:hover {
          background: #f5f7f9;
        }
      }
    }

    blockquote {
      margin: 12px 0;
      padding: 0 12px;
      border-left: 3px solid #dcdfe6;
      color: #4b5563;
    }

    a {
      color: #4c7ee9;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    h1, h2, h3, h4, h5, h6 {
      margin: 18px 0 10px;
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;

      &:first-child {
        margin-top: 0;
      }
    }

    // 设计稿强调文字：700 字重、色值 #1B1433
    strong {
      color: #1b1433;
      font-weight: 700;
    }

    em {
      font-style: italic;
    }

    hr {
      margin: 16px 0;
      border: 0;
      border-top: 1px solid #e3e5eb;
    }

    img {
      display: block;
      max-width: 100%;
      height: auto;
      margin: 12px 0;
      border-radius: 4px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .typing-indicator span,
  .progress-mark,
  .progress-mark .icon-sikao,
  .progress-text,
  .thinking-cursor,
  .reasoning-toggle .el-icon-loading,
  .success-tip.is-celebrating,
  .success-tip.is-celebrating .success-mark,
  .success-tip.is-celebrating .success-mark::before,
  .success-tip.is-celebrating .success-mark i,
  .success-tip.is-celebrating .success-label {
    animation: none !important;
  }

  .progress-roll-enter-active,
  .progress-roll-leave-active,
  .progress-mark,
  .progress-text {
    transition: none !important;
  }
}
</style>

<style lang="scss">
// 统一 AI 回答内容区(.markdown-body)里的滚动条为细样式。放在非 scoped 块、用普通类选择器，
// 以可靠命中 v-html 动态插入(表格/卡片等)的节点（它们没有 scoped 的 data-v）。
// .markdown-body 全项目仅本组件使用，作用域安全。
// 两套都写：scrollbar-width 是标准属性(Chrome 121+/Firefox 均支持)；::-webkit-scrollbar 兜底旧内核。
// 用具体元素(table/pre/div)而不是通配 *，Chrome 对 `*::-webkit-scrollbar` 有时不生效。
.markdown-body,
.markdown-body table,
.markdown-body pre,
.markdown-body div {
  scrollbar-width: thin;
  scrollbar-color: #dedee3 transparent;
}
.markdown-body::-webkit-scrollbar,
.markdown-body table::-webkit-scrollbar,
.markdown-body pre::-webkit-scrollbar,
.markdown-body div::-webkit-scrollbar {
  width: 4px !important;
  height: 4px !important;
}
.markdown-body::-webkit-scrollbar-thumb,
.markdown-body table::-webkit-scrollbar-thumb,
.markdown-body pre::-webkit-scrollbar-thumb,
.markdown-body div::-webkit-scrollbar-thumb {
  border-radius: 2px;
  background: #dedee3;
}
.markdown-body::-webkit-scrollbar-track,
.markdown-body table::-webkit-scrollbar-track,
.markdown-body pre::-webkit-scrollbar-track,
.markdown-body div::-webkit-scrollbar-track {
  background: transparent;
}
</style>
